"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
} from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { errorMessage } from "@/src/core/api/validation";
import { wishlistKeys } from "../api/queries";
import { WISHLIST_ERROR, WISHLIST_TOAST as TOAST } from "../services";
import { wishlistActions, wishlistStore } from "../store/wishlistStore";
import type { IWishlist } from "../types";
import ResponsiveDialog from "@/src/shared/components/ResponsiveDialog";
import WishlistNameForm, { type IWishlistNameValues } from "./WishlistNameForm";
import WishlistSaveList from "./WishlistSaveList";

/**
 * iOS navigation feel: the pushed screen slides in from the right over the one below,
 * which drifts left and dims; back reverses it. The sheet's height follows on the same
 * curve, so a short list growing into the taller form never snaps.
 */
const PUSH_TRANSITION = {
  type: "tween",
  duration: 0.35,
  ease: [0.32, 0.72, 0, 1],
} as const;
const SLIDE = {
  // The pushed (create) screen is always on top: entering forward, leaving backward.
  enter: (direction: number) =>
    direction > 0
      ? { x: "100%", opacity: 1, zIndex: 1 }
      : { x: "-30%", opacity: 0.6, zIndex: 0 },
  center: { x: 0, opacity: 1 },
  exit: (direction: number) =>
    direction > 0
      ? { x: "-30%", opacity: 0.6, zIndex: 0 }
      : { x: "100%", opacity: 1, zIndex: 1 },
};
/** The create form focuses its field once the slide is over (focus mid-slide makes it jump). */
const FOCUS_AFTER_SLIDE_MS = 380;
/** How far (px), or how fast (px/s), a right swipe must go to count as "back". */
const SWIPE_BACK_DISTANCE = 80;
const SWIPE_BACK_VELOCITY = 500;

/**
 * The save and create sheets, mounted once in `AppShell`; any heart opens them through
 * `wishlistStore.sheet`. Mirrors the app's `WishlistSaveSheet` / `WishlistCreateSheet`.
 */
const WishlistSheets: React.FC = () => {
  const { sheet, folders, savedIn, pending } = wishlistStore.useStore();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [busyFolderId, setBusyFolderId] = useState<number | null>(null);

  // The height of the screen on show, animated so the sheet resizes with the slide.
  // Only a new screen is observed; the leaving one detaching does not stop it.
  const [paneHeight, setPaneHeight] = useState<number | "auto">("auto");
  const observer = useRef<ResizeObserver | null>(null);
  const measurePane = useCallback((node: HTMLDivElement | null) => {
    if (!node || typeof ResizeObserver === "undefined") return;
    observer.current?.disconnect();
    observer.current = new ResizeObserver(([entry]) => {
      if (entry) setPaneHeight(entry.contentRect.height);
    });
    observer.current.observe(node);
  }, []);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: wishlistKeys.all });
  const fail = (error: unknown) =>
    enqueueSnackbar(errorMessage(error, WISHLIST_ERROR), {
      ...TOAST,
      variant: "error",
    });
  const saved = (name: string) =>
    enqueueSnackbar(`Saved to ${name}`, { ...TOAST, variant: "default" });

  const selectFolder = async (folder: IWishlist) => {
    if (sheet?.type !== "save") return;
    const { listingId, moveFrom } = sheet;
    const isChecked = (savedIn[listingId] ?? []).includes(folder.id);
    setBusyFolderId(folder.id);
    try {
      if (isChecked) {
        // A ticked folder untick: the sheet stays open as the "which lists" editor.
        await wishlistActions.remove(listingId, folder.id);
        refresh();
        return;
      }
      if (moveFrom !== undefined && moveFrom !== folder.id) {
        await wishlistActions.move(listingId, moveFrom, folder.id);
      } else {
        await wishlistActions.save(listingId, folder.id);
      }
      refresh();
      wishlistActions.closeSheet();
      saved(folder.name);
    } catch (error) {
      fail(error);
    } finally {
      setBusyFolderId(null);
    }
  };

  const create = async ({
    name,
    visibility,
    isDefault,
  }: IWishlistNameValues) => {
    const listingId = sheet?.listingId;
    try {
      const folder = await wishlistActions.create({
        name,
        visibility,
        is_default: isDefault,
        ...(listingId !== undefined ? { listing_id: listingId } : {}),
      });
      refresh();
      wishlistActions.closeSheet();
      if (listingId !== undefined) saved(folder.name);
    } catch (error) {
      fail(error);
      throw error;
    }
  };

  const isSave = sheet?.type === "save";
  // The create sheet opened from the save sheet can go back to it (button or swipe).
  const back =
    sheet?.type === "create" && sheet.fromSave && sheet.listingId !== undefined
      ? () =>
          wishlistActions.openSheet({
            type: "save",
            listingId: sheet.listingId as number,
          })
      : undefined;
  // Forward into create, backward into save.
  const direction = sheet?.type === "create" ? 1 : -1;

  // Swipe back from the left edge, as on iOS. Only a thin edge strip listens, so the
  // form and its field never carry drag styles (`user-select: none` broke the caret).
  const swipeX = useMotionValue(0);
  const swipe = useRef<{ startX: number; startTime: number } | null>(null);
  // A new create screen starts at the edge; a leaving one keeps its offset so it slides
  // on from where the finger let go.
  useEffect(() => {
    if (sheet?.type === "create") swipeX.set(0);
  }, [sheet?.type, swipeX]);
  const onEdgeDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!back) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    swipe.current = { startX: event.clientX, startTime: event.timeStamp };
  };
  const onEdgeMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!swipe.current) return;
    swipeX.set(Math.max(0, event.clientX - swipe.current.startX));
  };
  const onEdgeUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!swipe.current) return;
    const distance = event.clientX - swipe.current.startX;
    const elapsed = Math.max(1, event.timeStamp - swipe.current.startTime);
    swipe.current = null;
    if (
      distance > SWIPE_BACK_DISTANCE ||
      (distance / elapsed) * 1000 > SWIPE_BACK_VELOCITY
    ) {
      back?.();
    } else {
      void animate(swipeX, 0, PUSH_TRANSITION);
    }
  };

  return (
    <ResponsiveDialog
      isOpen={sheet !== null}
      onClose={wishlistActions.closeSheet}
      title={isSave ? "Save to wishlist" : "Create wishlist"}
      onBack={back}
    >
      <motion.div
        className="relative overflow-hidden"
        initial={false}
        animate={{ height: paneHeight }}
        transition={PUSH_TRANSITION}
      >
        <AnimatePresence initial={false} mode="popLayout" custom={direction}>
          {sheet && (
            <motion.div
              key={sheet.type}
              custom={direction}
              variants={SLIDE}
              initial="enter"
              animate="center"
              exit="exit"
              transition={PUSH_TRANSITION}
              ref={measurePane}
              // `relative` makes the variants' zIndex count. Without it the screen on
              // show drops under the leaving one (absolute, from popLayout) the moment
              // its slide ends and the transform goes, and the old list flashes over it.
              className={
                sheet.type === "create"
                  ? "relative bg-white shadow-[-12px_0_24px_-12px_rgba(0,0,0,0.18)]"
                  : "relative bg-white"
              }
            >
              <motion.div className="relative" style={{ x: swipeX }}>
                {back && sheet.type === "create" && (
                  <div
                    aria-hidden
                    onPointerDown={onEdgeDown}
                    onPointerMove={onEdgeMove}
                    onPointerUp={onEdgeUp}
                    onPointerCancel={onEdgeUp}
                    className="absolute inset-y-0 left-0 z-10 w-5 touch-pan-y"
                  />
                )}
                {sheet?.type === "save" ? (
                  <WishlistSaveList
                    folders={folders}
                    savedIn={savedIn[sheet.listingId] ?? []}
                    isPending={pending.includes(sheet.listingId)}
                    busyFolderId={busyFolderId}
                    onSelect={(folder) => void selectFolder(folder)}
                    onCreate={() =>
                      wishlistActions.openSheet({
                        type: "create",
                        listingId: sheet.listingId,
                        fromSave: true,
                      })
                    }
                  />
                ) : sheet?.type === "create" ? (
                  <WishlistNameForm
                    withOptions
                    submitLabel="Create"
                    focusDelayMs={sheet.fromSave ? FOCUS_AFTER_SLIDE_MS : 0}
                    onSubmit={create}
                  />
                ) : null}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </ResponsiveDialog>
  );
};

export default WishlistSheets;
